import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useAuth } from '@/lib/auth';
import { authExtraApi } from '@/lib/api';
import { toast } from 'sonner';
import { Sparkles, Mail } from 'lucide-react';
import { PORTAL } from '@/constants/testIds';

export default function PortalLoginPage() {
  const { login, setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [devToken, setDevToken] = useState('');
  const [params] = useSearchParams();

  // Handle magic-verify from URL
  useEffect(() => {
    const t = params.get('token');
    if (!t) return;
    (async () => {
      try {
        const r = await authExtraApi.magicVerify(t);
        localStorage.setItem('fc_token', r.token);
        setUser(r.user);
        toast.success(`Welcome, ${r.user.name}`);
        navigate('/portal');
      } catch (e) {
        toast.error('Magic link invalid or expired');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role !== 'customer') {
        toast.error('This login is for customers. Please use /login for admin/cleaner.');
        return;
      }
      toast.success(`Welcome, ${u.name}`);
      navigate('/portal');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  const demoCustomer = async () => {
    setLoading(true);
    try {
      const u = await login('customer@flexiclean.app', 'customer123');
      toast.success(`Welcome, ${u.name}`);
      navigate('/portal');
    } catch (e) {
      toast.error('Demo login failed');
    } finally { setLoading(false); }
  };

  const sendMagic = async () => {
    if (!email) { toast.error('Enter your email first'); return; }
    setLoading(true);
    try {
      const r = await authExtraApi.magicLinkRequest(email);
      setMagicSent(true);
      if (r.dev_token) setDevToken(r.dev_token);
      toast.success('If that email is registered, a login link is on its way.');
    } catch (e) {
      toast.error('Failed to send magic link');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-gradient-to-br from-[hsl(var(--background))] via-[hsl(var(--accent))] to-[hsl(var(--primary-subtle))]">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Sparkles className="h-5 w-5" /></div>
            <div className="font-display text-2xl font-semibold">FlexiClean</div>
          </div>
          <CardTitle className="text-xl">Customer portal</CardTitle>
          <CardDescription>View invoices, chat with us, book extras and more.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} data-testid={PORTAL.loginEmail} />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} data-testid={PORTAL.loginPassword} />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-10" data-testid={PORTAL.loginSubmit}>{loading ? 'Signing in…' : 'Sign in'}</Button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <div className="text-xs text-muted-foreground">or</div>
            <div className="h-px flex-1 bg-border" />
          </div>

          <Button type="button" variant="outline" onClick={sendMagic} disabled={loading || !email} className="w-full" data-testid={PORTAL.magicLinkRequest}>
            <Mail className="h-4 w-4 mr-2" />Email me a one-time login link
          </Button>
          {process.env.REACT_APP_DEMO_ENABLED !== 'false' && (
            <Button type="button" variant="secondary" onClick={demoCustomer} disabled={loading} className="mt-2 w-full">
              <Sparkles className="h-4 w-4 mr-2" />Explore the demo customer account
            </Button>
          )}
          {magicSent && (
            <div className="mt-3 rounded-md bg-muted p-3 text-xs text-muted-foreground">
              A one-time link has been sent to <b>{email}</b>. It expires in 20 minutes.
              {devToken && (
                <div className="mt-2">
                  <span className="text-[10px] uppercase tracking-wide">Dev mode:</span>{' '}
                  <Link to={`/portal/login?token=${devToken}`} className="font-mono text-primary hover:underline break-all">click here to open the link locally</Link>
                </div>
              )}
            </div>
          )}
          <div className="mt-6 text-center text-sm text-muted-foreground">
            Are you a team member? <Link to="/login" className="font-medium text-primary hover:underline">Team sign in</Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
